import { Drawer } from '@/components/modules';
import {
  DateRangePicker,
  type DateTimeRange,
} from '@/components/ui/date-range-picker';
import { useSiteLog } from '@/hooks/useSiteLog';
import type { SiteData } from '@/types/site';
import { useEffect, useState } from 'react';
import PageLoader from '../layouts/PageLoader';

// LogRecord 인터페이스를 제거하고 SiteLogItem 타입을 사용

interface SiteLogDrawerProps {
  readonly isOpen: boolean;
  readonly selectedSite: SiteData | null;
  readonly onClose: () => void;
}

const getFieldStatusColor = (status: string) => {
  switch (status) {
    case '정상':
      return 'bg-green-100 text-green-800';
    case '멈춤':
      return 'bg-yellow-100 text-yellow-800';
    case '에러':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getMotorColor = (operation: string) => {
  switch (operation) {
    case '운전':
      return 'text-green-600';
    case '정지':
      return 'text-red-600';
    case '대기':
      return 'text-blue-600';
    default:
      return 'text-gray-600';
  }
};

const getMotorIcon = (motorState: string) => {
  switch (motorState) {
    case '운전':
      return 'ri-play-circle-line';
    case '정지':
      return 'ri-stop-circle-line';
    default:
      return 'ri-pause-circle-line';
  }
};

export default function SiteLogDrawer({
  isOpen,
  selectedSite,
  onClose,
}: SiteLogDrawerProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [dateTimeRange, setDateTimeRange] = useState<
    DateTimeRange | undefined
  >();

  useEffect(() => {
    if (isOpen && !!selectedSite?.siteId) {
      setCurrentPage(1);
      setItemsPerPage(10);
      setDateTimeRange(undefined);
    }
  }, [isOpen, selectedSite?.siteId]);

  const startDate = dateTimeRange?.from
    ? (() => {
        const [hours, minutes] = (dateTimeRange.startTime ?? '00:00')
          .split(':')
          .map(Number);
        const d = new Date(
          dateTimeRange.from.getFullYear(),
          dateTimeRange.from.getMonth(),
          dateTimeRange.from.getDate(),
          hours,
          minutes,
          0
        );
        return d.toISOString();
      })()
    : undefined;

  const endDate = dateTimeRange?.to
    ? (() => {
        const [hours, minutes] = (dateTimeRange.endTime ?? '23:59')
          .split(':')
          .map(Number);
        const d = new Date(
          dateTimeRange.to.getFullYear(),
          dateTimeRange.to.getMonth(),
          dateTimeRange.to.getDate(),
          hours,
          minutes,
          59,
          999
        );
        return d.toISOString();
      })()
    : undefined;

  // 실제 API 호출을 사용하여 사이트 로그 데이터 가져오기
  const {
    logs,
    pagination,
    lowWaterLevel,
    highWaterLevel,
    isLoading,
    isError,
  } = useSiteLog({
    siteId: selectedSite?.siteId || '',
    page: currentPage,
    limit: itemsPerPage,
    startDate,
    endDate,
    enabled: isOpen && !!selectedSite?.siteId,
  });

  const handleDateRangeChange = (range: DateTimeRange | undefined) => {
    setDateTimeRange(range);
    setCurrentPage(1);
  };

  if (!isOpen || !selectedSite) return null;

  // 로딩 상태 처리
  if (isLoading) {
    return (
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        title={`${selectedSite.address} 상세데이터`}
        subtitle={selectedSite.name}
        width="w-[67vw]"
      >
        <div className="flex justify-center items-center h-64">
          <PageLoader />
        </div>
      </Drawer>
    );
  }

  // 에러 상태 처리
  if (isError) {
    return (
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        title={`${selectedSite.address} 상세데이터`}
        subtitle={selectedSite.name}
        width="w-[67vw]"
      >
        <div className="flex justify-center items-center h-64">
          <div className="text-red-500">
            로그 데이터를 불러오는데 실패했습니다.
          </div>
        </div>
      </Drawer>
    );
  }

  const headerActions = (
    <div className="flex items-center space-x-2">
      <DateRangePicker value={dateTimeRange} onChange={handleDateRangeChange} />
      <span className="text-sm text-gray-600">페이지당 항목:</span>
      <select
        value={itemsPerPage}
        onChange={(e) => {
          setItemsPerPage(Number(e.target.value));
          setCurrentPage(1);
        }}
        className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white pr-8"
      >
        <option value={5}>5개 항목</option>
        <option value={10}>10개 항목</option>
        <option value={15}>15개 항목</option>
        <option value={20}>20개 항목</option>
        <option value={25}>25개 항목</option>
      </select>
    </div>
  );

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={`${selectedSite.address} 상세데이터`}
      subtitle={selectedSite.name}
      width="w-[67vw]"
      headerActions={headerActions}
    >
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            실시간 상세 데이터
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  시각 (timestamp)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  현장 상태
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  모터 동작 상태
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  수위
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {logs.map((record) => (
                <tr key={record.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {record.timestampString}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getFieldStatusColor(record.fieldStatus)}`}
                      >
                        <i
                          className={`${record.fieldStatus === '에러' ? 'ri-alert-line' : 'ri-check-line'} w-3 h-3 mr-1`}
                        ></i>
                        {record.fieldStatus}
                      </span>
                      {record.fieldStatus === '에러' && (
                        <div className="text-xs text-gray-500 space-y-0.5">
                          {record.electricWaveError && <div>• 전파 이상</div>}
                          {record.overloadError && <div>• 과부하 이상</div>}
                          {record.undergroundWaterError && (
                            <div>• 지하수 이상</div>
                          )}
                          {selectedSite?.waterLevelSmsEnabled !== false &&
                            record.waterLevel < lowWaterLevel && (
                              <div>• 저수위 이상</div>
                            )}
                          {selectedSite?.waterLevelSmsEnabled !== false &&
                            record.waterLevel > highWaterLevel && (
                              <div>• 고수위 이상</div>
                            )}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div
                      className={`flex items-center space-x-2 ${getMotorColor(record.motorState)}`}
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className={getMotorIcon(record.motorState)}></i>
                      </div>
                      <span className="text-sm font-medium">
                        {record.motorState}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <div className="w-16 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${record.waterLevel}%` }}
                        ></div>
                      </div>
                      <span className="text-sm text-gray-900">
                        {record.waterLevel}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* 페이지네이션 */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center sm:justify-between justify-center">
              <div className="sm:flex hidden items-center space-x-4">
                <div className="text-sm text-gray-700">
                  {(pagination.currentPage - 1) * pagination.itemsPerPage + 1}
                  부터{' '}
                  {Math.min(
                    pagination.currentPage * pagination.itemsPerPage,
                    pagination.totalItems
                  )}
                  까지 (총 {pagination.totalItems}개 결과)
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center space-x-1"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-s-line"></i>
                  </div>
                  <span className="hidden sm:block">이전</span>
                </button>
                <div className="flex items-center space-x-1">
                  {(() => {
                    const pages = [];
                    const maxVisiblePages = 5;
                    let startPage = Math.max(
                      1,
                      currentPage - Math.floor(maxVisiblePages / 2)
                    );
                    const endPage = Math.min(
                      pagination.totalPages,
                      startPage + maxVisiblePages - 1
                    );

                    if (endPage - startPage + 1 < maxVisiblePages) {
                      startPage = Math.max(1, endPage - maxVisiblePages + 1);
                    }

                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <button
                          key={i}
                          onClick={() => setCurrentPage(i)}
                          className={`px-3 py-2 text-sm border rounded-lg cursor-pointer whitespace-nowrap ${
                            i === currentPage
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {i}
                        </button>
                      );
                    }

                    return pages;
                  })()}
                </div>
                <button
                  onClick={() =>
                    setCurrentPage(
                      Math.min(pagination.totalPages, currentPage + 1)
                    )
                  }
                  disabled={currentPage === pagination.totalPages}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center space-x-1"
                >
                  <span className="hidden sm:block">다음</span>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-s-line"></i>
                  </div>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Drawer>
  );
}
